/**
 * Keys to exclude from comparison
 */
const blockedKeys = ['json', 'landingDesign', 'custom'];

/**
 * Options for the diff mapper
 */
interface DiffMapperOptions {
	/**
	 * When true, flattens the diff structure using path strings as keys
	 */
	usePath?: boolean;
}

/**
 * Utility for deep comparison of objects to identify differences
 */
const deepDiffMapper = {
	// Diff types
	VALUE_CREATED: 'created',
	VALUE_UPDATED: 'updated',
	VALUE_DELETED: 'deleted',
	VALUE_UNCHANGED: 'unchanged',

	/**
	 * Flattens a nested diff object into a single-level object with path-based keys
	 * @param diff - The diff object to flatten
	 * @param basePath - The current path (used in recursion)
	 * @returns Flattened object with path keys
	 */
	flattenDiff: function (diff: any, basePath = ''): any {
		const result: any = {};

		for (const key in diff) {
			const currentPath = basePath ? `${basePath}.${key}` : key;
			const value = diff[key];

			// If it has a 'type' property, it's a leaf diff node
			if (value && typeof value === 'object' && 'type' in value) {
				result[currentPath] = value;
			}
			// If it's an object without a 'type', recurse
			else if (value && typeof value === 'object' && Object.keys(value).length > 0) {
				const flattenedNested = this.flattenDiff(value, currentPath);
				Object.assign(result, flattenedNested);
			}
		}

		return result;
	},

	/**
	 * Maps differences between two objects
	 * @param obj1 - First object to compare
	 * @param obj2 - Second object to compare
	 * @param options - Comparison options
	 * @param sender - Path identifier for nested properties (internal use)
	 * @returns Object containing differences
	 */
	map: function <T>(obj1: T | undefined, obj2: T | undefined, options: DiffMapperOptions | string = {}, sender = 'main') {
		// Handle backward compatibility where options used to be the sender string
		if (typeof options === 'string') {
			sender = options;
			options = {};
		}

		const { usePath = false } = typeof options === 'object' ? options : {};
		const isRoot = sender === 'main';

		// Handle edge cases
		if (obj1 === undefined && obj2 === undefined) return {};

		// Modified deletion handling:
		if (obj2 === undefined) {
			if ((this.isObject(obj1) || this.isArray(obj1)) && !this.isDate(obj1)) {
				const diff: { [key: string]: any } = {};
				for (const key in obj1) {
					if (blockedKeys.includes(key)) continue;
					if (this.isFunction((obj1 as any)[key])) continue;
					const nestedPath = `${sender}/${key}`;
					diff[key] = this.map((obj1 as any)[key], undefined, options, nestedPath);
				}
				return diff;
			}
			return { type: this.VALUE_DELETED, prev: obj1 };
		}

		if (this.isFunction(obj1) || this.isFunction(obj2)) return {};

		// Handle primitive values
		if (this.isValue(obj1) && this.isValue(obj2)) {
			return {
				type: this.compareValues(obj1, obj2),
				prev: obj1,
				data: obj2,
			};
		}

		// Compare objects recursively
		const diff: { [key: string]: any } = {};

		// Check properties in first object
		if (obj1 && typeof obj1 === 'object') {
			for (const key in obj1) {
				if (blockedKeys.includes(key)) continue;
				if (this.isFunction((obj1 as any)[key])) continue;

				const value2 = obj2 ? (obj2 as any)[key] : undefined;
				const nestedPath = `${sender}/${key}`;
				diff[key] = this.map((obj1 as any)[key], value2, options, nestedPath);
			}
		}

		// Check for properties in second object that aren't in first
		if (obj2 && typeof obj2 === 'object') {
			for (const key in obj2) {
				if (blockedKeys.includes(key)) continue;
				if (this.isFunction((obj2 as any)[key]) || diff[key] !== undefined) continue;

				const nestedPath = `${sender}/${key}`;
				diff[key] = this.map(undefined, (obj2 as any)[key], options, nestedPath);
			}
		}

		// If using path option and we're at the root, flatten the final diff
		if (usePath && isRoot) {
			return this.flattenDiff(diff);
		}

		return diff;
	},

	/**
	 * Compares two values and returns the difference type
	 */
	compareValues: function (value1: any, value2: any) {
		if (value1 === value2) return this.VALUE_UNCHANGED;
		if (value1 === undefined) return this.VALUE_CREATED;
		if (value2 === undefined) return this.VALUE_DELETED;

		// Special handling for Date objects
		if (this.isDate(value1) && this.isDate(value2) && value1.getTime() === value2.getTime()) {
			return this.VALUE_UNCHANGED;
		}

		return this.VALUE_UPDATED;
	},

	// Type checking utilities
	isFunction: (x: any): boolean => x !== undefined && Object.prototype.toString.call(x) === '[object Function]',

	isArray: (x: any): boolean => x !== undefined && Object.prototype.toString.call(x) === '[object Array]',

	isDate: (x: any): boolean => x !== undefined && Object.prototype.toString.call(x) === '[object Date]',

	isObject: (x: any): boolean => x !== undefined && Object.prototype.toString.call(x) === '[object Object]',

	isValue: function (x: any): boolean {
		return !this.isObject(x) && !this.isArray(x);
	},
};

export default deepDiffMapper;

// Add it to window for testing
if (typeof window !== 'undefined') {
	// @ts-ignore
	window.deepDiffMapper = deepDiffMapper;
}
